import { createClient } from '@supabase/supabase-js';
import { supabase } from './supabase'; // Feedback tool database

// Reporting database client (separate from feedback tool database)
// Use environment variables if available, otherwise use hardcoded values
const REPORTING_SUPABASE_URL = import.meta.env.VITE_REPORTING_SUPABASE_URL || 'https://uydhsjvwrgupgfjevqsz.supabase.co';
const REPORTING_SUPABASE_ANON_KEY = import.meta.env.VITE_REPORTING_SUPABASE_ANON_KEY || 'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5';

export const reportingSupabase = createClient(REPORTING_SUPABASE_URL, REPORTING_SUPABASE_ANON_KEY);

export interface PlatformMetrics {
  spend: number;
  budget: number;
  status: string;
  end_date: string | null;
  campaign_count: number;
}

export interface CampaignMetrics {
  spend: number;
  budget: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  report_url?: string;
  campaign_name?: string;
  // Per-platform breakdowns
  meta?: PlatformMetrics;
  tiktok?: PlatformMetrics;
  google?: PlatformMetrics;
}

export interface ReportRecommendation {
  campaign_name: string;
}

export interface ReportSearchResult {
  id: string;
  name: string;
  share_url: string | null;
}

// Search available reports in the reporting DB by artist / release name
export async function searchAvailableReports(artistName: string, releaseName?: string): Promise<ReportSearchResult[]> {
  try {
    // Search for reports matching "Artist - Release" pattern
    // If releaseName is provided, require BOTH artist and release to match
    const artist = artistName?.trim();
    const release = releaseName?.trim();

    if (!artist || artist.length < 2) return [];

    const { data, error } = await reportingSupabase
      .from('reports')
      .select('id, name, share_url')
      .ilike('name', `%${artist}%`)
      .limit(50) as { data: ReportSearchResult[] | null; error: any };

    if (error) {
      console.error('Error searching reports:', error);
      return [];
    }

    let results = (data || []);

    // If release name is provided, filter to only those that also match the release name
    if (release && release.length >= 2) {
      results = results.filter(r =>
        r.name.toLowerCase().includes(release.toLowerCase())
      );
    }

    return results;
  } catch (error) {
    console.error('Error in searchAvailableReports:', error);
    return [];
  }
}

export async function fetchCampaignMetrics(campaignName: string, artistName?: string, projectId?: string, linkedReportIds?: string[]): Promise<CampaignMetrics | null> {
  try {
    // Fetch metrics for campaign
    
    // Cache is currently bypassed because the cache table doesn't store
    // per-platform breakdowns (meta/tiktok/google). The edge function fetches
    // live Meta spend from the Graph API anyway, so caching has limited value.
    // TODO: add a platform_data JSONB column to cache table if performance needs it.

    // Cache miss or stale - fetch from edge function
    const response = await fetch(`${REPORTING_SUPABASE_URL}/functions/v1/fetch-campaign-metrics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        campaign_name_search: campaignName,
        artist_name_search: artistName || null,
        linked_report_ids: linkedReportIds || null,
      }),
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      console.error('❌ Error fetching campaign metrics:', response.status, response.statusText, errorBody);
      return null;
    }

    const data = await response.json();

    if (!data) {
      return null;
    }

    const metrics: CampaignMetrics = {
      spend: parseFloat(data.spend || '0'),
      budget: parseFloat(data.lifetime_budget || data.daily_budget || '0'),
      impressions: parseInt(data.impressions || '0'),
      clicks: parseInt(data.clicks || '0'),
      conversions: parseInt(data.conversions || '0'),
      ctr: parseFloat(data.ctr || '0'),
      report_url: data.report_url || null,
      campaign_name: data.campaign_name || null,
      meta: data.meta ? {
        spend: parseFloat(data.meta.spend || '0'),
        budget: parseFloat(data.meta.budget || '0'),
        status: data.meta.status || 'unknown',
        end_date: data.meta.end_date || null,
        campaign_count: parseInt(data.meta.campaign_count || '0'),
      } : undefined,
      tiktok: data.tiktok ? {
        spend: parseFloat(data.tiktok.spend || '0'),
        budget: parseFloat(data.tiktok.budget || '0'),
        status: data.tiktok.status || 'unknown',
        end_date: data.tiktok.end_date || null,
        campaign_count: parseInt(data.tiktok.campaign_count || '0'),
      } : undefined,
      google: data.google ? {
        spend: parseFloat(data.google.spend || '0'),
        budget: parseFloat(data.google.budget || '0'),
        status: data.google.status || 'unknown',
        end_date: data.google.end_date || null,
        campaign_count: parseInt(data.google.campaign_count || '0'),
      } : undefined,
    };

    // Cache write is currently disabled because the cache table doesn't store
    // per-platform breakdowns (meta/tiktok/google). See note above about cache read.

    return metrics;
  } catch (error) {
    console.error('❌ Error in fetchCampaignMetrics:', error);
    return null;
  }
}

export async function getReportRecommendations(campaignName: string, artistName?: string): Promise<ReportRecommendation[]> {
  try {
    const { data, error } = await reportingSupabase.rpc('get_report_recommendations', {
      campaign_name_search: campaignName,
      artist_name_search: artistName || null
    }) as { data: any; error: any };

    if (error) {
      console.error('Error fetching report recommendations:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getReportRecommendations:', error);
    return [];
  }
}

export interface LexofficeInvoiceData {
  invoice_id: string;
  voucher_number: string | null;
  voucher_date: string | null;
  voucher_status: string | null;
  media_budget_net: number;
  media_budget_gross: number;
  agency_fee_net: number;
  agency_fee_gross: number;
  total_net: number;
  total_gross: number;
  line_items: { name: string; net_amount: number; gross_amount: number }[];
}

/**
 * Fetch a Lexoffice invoice and extract the Media Budget line item
 * (excluding the agency fee / "Release Advertising" line).
 * Calls the fetch-lexoffice-invoice edge function on the feedbacktool project.
 *
 * Lexoffice's API is aggressively rate-limited: bursts return HTTP 500
 * ("Internal server error or rate limit exceeded") or 429. The dashboard
 * mounts one ProjectCard per project and each card previously fetched its
 * invoice in parallel, blowing through the limit on every load/refresh.
 * All invoice fetches are therefore serialized through a queue with a
 * minimum interval between calls, and results are cached per invoice id so
 * re-renders / repeated fetches (including 404s for missing invoices) don't
 * re-hit the API. Transient failures (429/500) are NOT cached so they can
 * retry on the next dashboard refresh.
 */
const lexofficeCache = new Map<string, Promise<LexofficeInvoiceData | null>>();
let lexofficeQueue: Promise<unknown> = Promise.resolve();
const LEXOFFICE_MIN_INTERVAL_MS = 300; // ~4 req/s ceiling, under Lexoffice's limit

function enqueueLexoffice<T>(task: () => Promise<T>): Promise<T> {
  const run = lexofficeQueue.then(async () => {
    await new Promise((r) => setTimeout(r, LEXOFFICE_MIN_INTERVAL_MS));
    return task();
  });
  lexofficeQueue = run.catch(() => undefined);
  return run;
}

export function fetchLexofficeInvoice(invoiceId: string): Promise<LexofficeInvoiceData | null> {
  const cached = lexofficeCache.get(invoiceId);
  if (cached) return cached;

  const p = enqueueLexoffice(async () => {
    let data: LexofficeInvoiceData | null = null;
    let permanent = false;
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/fetch-lexoffice-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({ invoice_id: invoiceId }),
      });

      if (!response.ok) {
        // 404 = invoice doesn't exist (permanent); 429/500 = rate limit (transient)
        permanent = response.status >= 400 && response.status < 500 && response.status !== 429;
        // Silenced — 404s are expected for deleted/draft invoices
      } else {
        const body = await response.json();
        if (!body || body.error) {
          // Silenced — error response from Lexoffice (deleted/invalid invoice)
          permanent = true;
        } else {
          data = {
            invoice_id: body.invoice_id,
            voucher_number: body.voucher_number,
            voucher_date: body.voucher_date,
            voucher_status: body.voucher_status,
            media_budget_net: parseFloat(body.media_budget_net || '0'),
            media_budget_gross: parseFloat(body.media_budget_gross || '0'),
            agency_fee_net: parseFloat(body.agency_fee_net || '0'),
            agency_fee_gross: parseFloat(body.agency_fee_gross || '0'),
            total_net: parseFloat(body.total_net || '0'),
            total_gross: parseFloat(body.total_gross || '0'),
            line_items: body.line_items || [],
          };
          permanent = true;
        }
      }
    } catch (error) {
      // Network/timeout errors silenced — non-critical for UI
    }

    if (permanent) {
      lexofficeCache.set(invoiceId, Promise.resolve(data));
    } else if (lexofficeCache.get(invoiceId) === p) {
      // Transient failure (rate limit etc.) — allow a retry later
      lexofficeCache.delete(invoiceId);
    }
    return data;
  });

  // Dedupe concurrent fetches of the same invoice while in flight
  lexofficeCache.set(invoiceId, p);
  return p;
}

export interface UpdateMetaBudgetResult {
  success: boolean;
  campaign_id: string;
  new_budget_cents: number;
  new_budget_euros: number;
  budget_type: string;
  error?: string;
}

/**
 * Updates a Meta campaign's budget via the Graph API.
 * Calls the update-meta-campaign-budget edge function on the feedbacktool project.
 * @param campaignId The Meta campaign ID
 * @param newBudgetCents The new total budget in cents (e.g. 65000 = 650€)
 */
export async function updateMetaCampaignBudget(campaignId: string, newBudgetCents: number): Promise<UpdateMetaBudgetResult | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/update-meta-campaign-budget`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ campaign_id: campaignId, new_budget_cents: newBudgetCents }),
    });

    const data = await response.json();
    if (!response.ok) {
      console.error('❌ Error updating Meta campaign budget:', data);
      return { success: false, campaign_id: campaignId, new_budget_cents: newBudgetCents, new_budget_euros: newBudgetCents / 100, budget_type: 'unknown', error: data.error || 'Unknown error' };
    }

    return {
      success: true,
      campaign_id: data.campaign_id,
      new_budget_cents: data.new_budget_cents,
      new_budget_euros: data.new_budget_euros,
      budget_type: data.budget_type,
    };
  } catch (error) {
    console.error('❌ Error in updateMetaCampaignBudget:', error);
    return null;
  }
}

export async function getProjectCampaignMetrics(projectId: string): Promise<CampaignMetrics | null> {
  try {
    console.log(`🔍 Fetching combined metrics for project: "${projectId}"`);
    
    const { data, error } = await reportingSupabase.rpc('get_project_campaign_metrics', {
      p_project_id: projectId
    }) as { data: any; error: any };

    if (error) {
      console.error('❌ Error fetching project campaign metrics:', error);
      return null;
    }

    console.log(`📊 Raw project data received:`, data);

    if (!data) {
      console.log(`⚠️ No data found for project: "${projectId}"`);
      return null;
    }

    const metrics = {
      spend: parseFloat(data.spend || '0'),
      budget: parseFloat(data.lifetime_budget || data.daily_budget || '0'),
      impressions: parseInt(data.impressions || '0'),
      clicks: parseInt(data.clicks || '0'),
      conversions: parseInt(data.conversions || '0'),
      ctr: parseFloat(data.ctr || '0'),
      report_url: data.report_url || null,
      campaign_name: data.campaign_name || null,
    };

    console.log(`✅ Parsed project metrics:`, metrics);
    return metrics;
  } catch (error) {
    console.error('❌ Error in getProjectCampaignMetrics:', error);
    return null;
  }
}
